import { computed, h } from 'vue';

import useSortFilter from './useSortFilter';
import useFacet from './useFacet';
import useScrollToLast from './useScrollToLast';

export default function useMultiSelectDefault(props, checkboxRef) {
  const {
    replaceFacetSelectedValues,
    facet,
    facetName,
    isSlideoutMode,
    checkboxSpacing,
    isDropdownMode,
    isMobileInLandscape,
    isMobile,
  } = useFacet(props);

  const { facetByName, updateFacet, rightSideDrawer } = useSortFilter();

  const valueRenderer = ((val) => {
    let checkboxVal = { ...val };
    /* istanbul ignore else */
    if (props.customValueRenderer) {
      checkboxVal = props.customValueRenderer(checkboxVal);
    }
    /* istanbul ignore else */
    if (checkboxVal) {
      /* istanbul ignore else */
      if (checkboxVal.count > 0 || checkboxVal.count === 0) { // avoiding problem with null
        const count = h('span', { class: 'valueCount' }, ` (${checkboxVal.count})`);
        const label = typeof (checkboxVal.label) === 'string' ? h('span', { class: 'valueLabelName' }, checkboxVal.label) : checkboxVal.label;
        checkboxVal.ariaLabel = `${checkboxVal.label} (${checkboxVal.count})`;
        checkboxVal.label = h('span', { class: 'valueLabel', title: `${checkboxVal.label} (${checkboxVal.count})` }, [label, count]);
        checkboxVal.labelType = 'html';
        const idString = `${props.facet.name} ${checkboxVal.value}`;
        const encodedIdString = (str) => str.replace(/[^A-Za-z0-9_]/g, (i) => `_$${i.charCodeAt(0)}`);
        checkboxVal.id = encodedIdString(idString);
      }
    }
    return checkboxVal;
  });

  const targetFacet = computed(() => {
    const useFacetFromSelections = isSlideoutMode.value && !facet.value.parent && !facet.value.alwaysDropdown && !props.isDynamic;
    // using store facet for facets that need to be rendered on server side
    return useFacetFromSelections && !props.renderDuringSsr ? facetByName(facetName.value) : facet.value;
  });

  const criteriaCheckbox = computed(() => [...(targetFacet.value?.selectedValues || [])]);

  const criteriaOptions = computed(() => (targetFacet.value?.values || []).map(valueRenderer).filter(Boolean));

  const isBelowMaxHeight = computed(() => (
    criteriaOptions.value ? criteriaOptions.value.length * 24 < 224 : true
  ));

  const updateCheckboxCriteria = (selected = facet.value.selectedValues, callUpdateSelected = true) => {
    const selectedValues = (props.updateSelected && callUpdateSelected)
      ? props.updateSelected(selected, facetName.value, criteriaOptions.value)
      : selected;

    /* istanbul ignore else */
    if (!selectedValues?.prevent) {
      if (isSlideoutMode.value && !facet.value.alwaysDropdown) {
        // slideout update
        updateFacet({
          name: facetName.value,
          selectedValues,
          parent: facet.value.parent,
        });
      } else {
        // store update
        const {
          name, displayName, facetType,
        } = facet.value;
        const selectedFacet = {
          name, selectedValues, displayName, facetType,
        };
        replaceFacetSelectedValues(selectedFacet);
      }
    }
  };

  const updateCheckboxCriteriaDynamic = (selected = facet.value.selectedValues) => {
    const facetSelectedValues = props.updateSelected
      ? props.updateSelected(selected, facetName.value, criteriaOptions.value)
      : selected;

    // use parent facet in case existing e.g Size facet
    const sourceStore = props.parentFacet ?? facet.value;
    let selectedValues = [];
    if (selected.length) {
      // use parent values if exist
      selectedValues = props.parentFacet ? props.parentFacet.selectedValues : facetSelectedValues;
    }

    const {
      name, displayName, facetType,
    } = sourceStore;

    const selectedFacet = {
      name, selectedValues, displayName, facetType,
    };

    replaceFacetSelectedValues(selectedFacet);
  };

  useScrollToLast(checkboxRef, props.scrollToSelected, criteriaCheckbox);

  const isNotMobile = computed(() => !isMobileInLandscape.value && !isMobile.value);
  const disableScroll = computed(() => (isBelowMaxHeight.value && isDropdownMode.value ? 'disable-scroll' : ''));
  const slideoutHeightFit = computed(() => (!rightSideDrawer.value && (isMobile.value || isMobileInLandscape.value) ? 'slideout-height-fit' : ''));
  const sizeShow = computed(() => (!rightSideDrawer.value && isNotMobile.value && props.facet.parent === 'Size' ? 'size-show' : ''));
  const colorShow = computed(() => (!rightSideDrawer.value && !isMobileInLandscape.value && props.facet.name === 'Color' ? 'color-show' : ''));

  const exportedObject = {
    isBelowMaxHeight,
    criteriaCheckbox,
    criteriaOptions,
    updateCheckboxCriteria: props.isDynamic ? updateCheckboxCriteriaDynamic : updateCheckboxCriteria,
    checkboxSpacing,
    disableScroll,
    slideoutHeightFit,
    sizeShow,
    colorShow,
  };

  /* istanbul ignore else */
  if (props.onInit) {
    props.onInit(exportedObject);
  }

  return exportedObject;
}
